import {useEffect} from "react";
import {useTheme} from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {grey} from "@mui/material/colors";
import io from "socket.io-client";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import * as React from "react";
import {UserContext} from "../../App";
import HPOTrainingStatus from "./HPOTrainingStatus";
import AFSTrainingStatus from "./AFSTrainingStatus";

export default function PipelineStatus(props) {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();
    // get current URL
    const location = useLocation();
    // get experiment methods
    const {
        storedUid,
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        fetchExperiments,
    } = React.useContext(UserContext);

    // create socket connection
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
        path: "/socket",
        forceNew: true,
        autoConnect: false,
        timeout: 50000,
        transports: ["websocket", "polling", "flashsocket"],
    });
    const [socketRoomData, setSocketRoomData] = React.useState({});
    // needed for routing to inspection site
    const navigate = useNavigate();

    const [pipelineSteps, setPipelineSteps] = React.useState([""]);
    const [stepFinishStatus, setStepFinishStatus] = React.useState([0]);

    const [activeStep, setActiveStep] = React.useState(0);

    const handleFinish = () => {
        navigate("/analyze");
    };

    const handleStep = (step) => () => {
        setActiveStep(step);
    };

    /* SOCKET CONNECTION
      The socket.io connection is used, to update the processing progress
      */

    // connect to socket, when the current dataset is gathered
    useEffect(() => {
        // get run name from url
        let runName = window.location.pathname.split("/").pop();
        const expName = decodeURIComponent(
            window.location.pathname.split(`/pipeline/${runName}`)[0].split("/").pop()
        );
        runName = decodeURIComponent(runName);
        if (expName === experiments[currentExperimentIdx].experimentName) {
            socket.connect();
            socket.on("connect", () => {
                const roomData = {
                    uid: storedUid,
                    datasetName: datasets[currentDatasetIdx].datasetName,
                    experimentName: experiments[currentExperimentIdx].experimentName,
                    runName,
                };
                setSocketRoomData(roomData);
                // this method will be triggered by the server
                socket.on("update-pipeline", (data) => {
                    setPipelineSteps(data.stepsAndCompletionStatus.stepNames);
                    // from all steps, get the last one in list that has been set to finished
                    const finishedSteps = data.stepsAndCompletionStatus.stepStatus
                        .map((status, idx) => (status === true ? idx : -1))
                        .filter((idx) => idx !== -1);
                    setActiveStep(
                        data.stepsAndCompletionStatus.stepNames[finishedSteps.length]
                    );

                    setStepFinishStatus(data.stepsAndCompletionStatus.stepStatus);
                });
                socket.on("run-completed", () => {
                    socket.disconnect();
                    fetchExperiments({latest: props.experimentName});
                });

                // create room for current user
                socket.emit("create-pipeline-room", roomData);
            });
        }
    }, [
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        location.pathname,
    ]);

    // Helper function to determine the progress component to render
    const renderStepComponent = (step) => {
        const stepName = String(step);

        if (stepName.startsWith("HPO")) {
            return (
                <HPOTrainingStatus
                    pipelineRoom={socketRoomData}
                    socket={socket}
                    stepName={step}
                />
            );
        }
        if (stepName.startsWith("AFS")) {
            return (
                <AFSTrainingStatus
                    pipelineRoom={socketRoomData}
                    socket={socket}
                    stepName={step}
                />
            );
        }
        return (
            {
                Initializing: (
                    <Grid container>{t("pipelineStatus.InitializingInfo")}</Grid>
                ),
            }[stepName] || null
        ); // Default to `null` if no match
    };

    React.useEffect(
        () =>
            // clean up WebSocket connection on component unmount
            () => {
                socket.disconnect();
            },
        []
    );

    return (
        <Grid
            sx={{
                overflowX: "hidden", // add this
                width: "100%",
            }}
        >
            <Grid
                id="processing-status-container"
                sx={{
                    padding: theme.spacing(4), // instead of spacing(8, 8)

                    overflowX: "hidden", // also add here
                }}
            >
                <Typography variant="h4" gutterBottom>
                    {t("pipelineStatus.PageTitle")}
                    <br/>
                </Typography>
                <Typography variant="body2">
                    {t("pipelineStatus.PageDescription")}
                </Typography>
                <br/>

                <Grid
                    container
                    spacing={2}
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="start"
                    sx={{
                        width: "100%", // allow it to adapt to the parent
                        maxWidth: "100%", // never overflow
                        overflowX: "hidden", // hide any potential horizontal scroll
                    }}
                >
                    {pipelineSteps?.length === 0 ? (
                        <Grid item xs={12}>
                            <CircularProgress/>
                        </Grid>
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <Box sx={{width: "100%"}}>
                                    <Stepper
                                        nonLinear
                                        activeStep={pipelineSteps.indexOf(activeStep)}
                                        alternativeLabel
                                    >
                                        {pipelineSteps.map((label, index) => (
                                            <Step
                                                key={label}
                                                completed={Boolean(stepFinishStatus[index])}
                                            >
                                                <StepButton onClick={handleStep(label)}>
                                                    {label}
                                                </StepButton>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>
                            </Grid>
                            <Grid item xl={10} xs={12} sx={{margin: theme.spacing(0, 4)}}>
                                {renderStepComponent(activeStep)}
                            </Grid>
                            <Grid item xl={12} xs={12}>
                                {Math.min(...stepFinishStatus) === 1 ? (
                                    <Paper
                                        square
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            marginTop: theme.spacing(3),
                                            background: grey[100],
                                        }}
                                    >
                                        <Typography>
                                            {t("trainingStatus.CompletedMessage")}
                                        </Typography>
                                        <Button
                                            onClick={handleFinish}
                                            sx={{mt: 1, mr: 1}}
                                            variant="contained"
                                            color="primary"
                                        >
                                            {t("trainingStatus.ProceedButton")}
                                        </Button>
                                    </Paper>
                                ) : (
                                    ""
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
}
